import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  ExternalLink,
  FileCode,
  Folder,
  Video,
  ShoppingBag,
  Download,
  RefreshCw,
  Upload,
  Edit2,
  Store,
  X,
  Save,
  Trash2,
  CheckCircle2,
  AlertCircle,
  ArrowRight,
  Layers,
  Plus
} from "lucide-react";
import { pb } from "../../services/pocketbase";
import { produtoDadosService, getArquivosArray } from "../../services/produtoDadosService";

/**
 * SecaoModelagemCad.js — Registro na Área de Modelagem CAD, Arquivos 3D, Imagens e Links
 *
 * Funcionalidades:
 * - Indica claramente se o SKU possui ou não registro na área de Modelagem CAD (com botão para ir até o fluxo de lá)
 * - Exibição e edição de Links (Loja, Mercado Livre, Google Drive, YouTube)
 * - Exibição, upload, download e exclusão de arquivos de modelagem 3D e imagens (locais e do registro CAD)
 */
export default function SecaoModelagemCad({
  skuPai,
  item,
  activeCategoryName,
  activeTipoJoiaName,
  onProdutoDadosUpdated,
  onNavigateToCad
}) {
  const [produtoDados, setProdutoDados] = useState(null);
  const [cadRecord, setCadRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creatingCad, setCreatingCad] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [deletingFile, setDeletingFile] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [savingLinks, setSavingLinks] = useState(false);

  // Form States para Edição de Links e Observações
  const [formLinks, setFormLinks] = useState({
    storeUrl: "",
    mlUrl: "",
    hasMlAd: false,
    driveUrl: "",
    youtubeUrl: "",
    observacoes: ""
  });

  // Carrega dados do PocketBase (produto_dados e modelagens)
  const fetchDados = useCallback(async () => {
    if (!skuPai) return;
    setLoading(true);
    try {
      const [pdRec, cadRecs] = await Promise.all([
        produtoDadosService.getProdutoDados(skuPai, { expand: "categoria,tipo_joia" }),
        pb.collection("modelagens").getFullList({
          filter: `sku = "${skuPai}" || sku ~ "${skuPai}-"`,
          sort: "-updated",
          expand: "responsavel"
        })
      ]);

      // Prioriza match exato pelo skuPai
      const exactCad = (cadRecs || []).find(r => (r.sku || "").toUpperCase().trim() === skuPai);
      const cadRec = exactCad || (cadRecs && cadRecs.length > 0 ? cadRecs[0] : null);

      setProdutoDados(pdRec);
      setCadRecord(cadRec);

      const links = pdRec?.links_legado || {};
      const finalStoreUrl = links.storeUrl || cadRec?.link_produto || "";

      setFormLinks({
        storeUrl: finalStoreUrl,
        mlUrl: links.mlUrl || "",
        hasMlAd: !!(links.hasMlAd || links.mlUrl || cadRec?.mercado_livre),
        driveUrl: links.driveUrl || "",
        youtubeUrl: links.youtubeUrl || "",
        observacoes: pdRec?.observacoes_internas || cadRec?.observacoes || ""
      });
    } catch (err) {
      console.warn("Erro ao buscar dados em SecaoModelagemCad:", err);
    } finally {
      setLoading(false);
    }
  }, [skuPai]);

  useEffect(() => {
    fetchDados();
  }, [fetchDados]);

  const linksLegado = produtoDados?.links_legado || {};
  const arquivosModelagem = getArquivosArray(produtoDados?.arquivos_modelagem);
  const arquivosImagens = getArquivosArray(produtoDados?.arquivos_imagens);

  // Arquivos vindos do registro CAD (modelagens.arquivos)
  const cadFilesList = useMemo(() => {
    if (!cadRecord || !cadRecord.arquivos) return [];
    const files = getArquivosArray(cadRecord.arquivos);
    return files.map(file => ({
      name: typeof file === "string" ? file : file.name,
      origin: "CAD",
      record: cadRecord
    }));
  }, [cadRecord]);

  // Lista unificada de Imagens / Zip do Produto
  const imagesFilesList = useMemo(() => {
    const list = (arquivosImagens || []).map(file => ({
      name: typeof file === "string" ? file : file.name,
      origin: "Local",
      record: produtoDados
    }));

    const cadFiltered = cadFilesList.filter(file => {
      const ext = file.name.split('.').pop()?.toLowerCase();
      return ["zip", "rar", "tar", "gz", "png", "jpg", "jpeg", "webp", "gif"].includes(ext);
    });

    return [...list, ...cadFiltered];
  }, [arquivosImagens, cadFilesList, produtoDados]);

  // Lista unificada de Arquivos de Modelagem 3D
  const modelagemFilesList = useMemo(() => {
    const list = (arquivosModelagem || []).map(file => ({
      name: typeof file === "string" ? file : file.name,
      origin: "Local",
      record: produtoDados
    }));

    const cadFiltered = cadFilesList.filter(file => {
      const ext = file.name.split('.').pop()?.toLowerCase();
      return ["stl", "3dm", "obj", "pdf"].includes(ext);
    });

    return [...list, ...cadFiltered];
  }, [arquivosModelagem, cadFilesList, produtoDados]);

  // Criar registro na área de Modelagem CAD caso não exista e ir para lá
  const handleCreateAndGoToCad = async () => {
    if (!skuPai || creatingCad) return;
    setCreatingCad(true);
    try {
      const nomeProd = item?.name || `Produto ${skuPai}`;
      const newCad = await pb.collection("modelagens").create({
        sku: skuPai,
        nome_produto: nomeProd,
        status: "pendente_aprovacao",
        prioridade: "media",
        responsavel: pb.authStore.model?.id || "",
        tipo_joia: activeTipoJoiaName || "Anel",
        categoria: activeCategoryName && activeCategoryName !== "Não Definido" ? activeCategoryName : "Formatura",
        observacoes: "Criado a partir da Ficha do Produto."
      });
      setCadRecord(newCad);
      if (onNavigateToCad) {
        onNavigateToCad(skuPai);
      }
    } catch (err) {
      alert("Erro ao criar registro na Modelagem CAD: " + err.message);
    } finally {
      setCreatingCad(false);
    }
  };

  // Salvar Links e Observações
  const handleSaveLinks = async (e) => {
    e.preventDefault();
    if (!skuPai) return;
    setSavingLinks(true);
    try {
      const storeUrlClean = formLinks.storeUrl.trim();
      const updatedLinks = {
        ...linksLegado,
        storeUrl: storeUrlClean,
        mlUrl: formLinks.mlUrl.trim(),
        hasMlAd: formLinks.hasMlAd || !!formLinks.mlUrl.trim(),
        driveUrl: formLinks.driveUrl.trim(),
        youtubeUrl: formLinks.youtubeUrl.trim(),
        updatedAt: new Date().toISOString()
      };

      const payload = {
        links_legado: updatedLinks,
        observacoes_internas: formLinks.observacoes.trim()
      };

      const updated = await produtoDadosService.upsertProdutoDados(skuPai, payload, { expand: "categoria,tipo_joia" });
      setProdutoDados(updated);
      if (onProdutoDadosUpdated) onProdutoDadosUpdated(updated);

      if (cadRecord) {
        const updatedCad = await pb.collection("modelagens").update(cadRecord.id, {
          link_produto: storeUrlClean,
          mercado_livre: !!(formLinks.hasMlAd || formLinks.mlUrl.trim())
        });
        setCadRecord(updatedCad);
      }

      setIsEditModalOpen(false);
    } catch (err) {
      alert("Erro ao salvar links e detalhes: " + err.message);
    } finally {
      setSavingLinks(false);
    }
  };

  // Upload Manual de Arquivos 3D (STL/3DM)
  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !skuPai) return;

    setUploading(true);
    try {
      const updated = await produtoDadosService.uploadModelagemFiles(skuPai, files);
      if (updated) {
        setProdutoDados(updated);
        if (onProdutoDadosUpdated) onProdutoDadosUpdated(updated);
      }
    } catch (err) {
      alert("Erro ao fazer upload dos arquivos 3D: " + err.message);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  // Upload Manual de Arquivos de Imagem (ZIP/RAR/Imagens)
  const handleImagesUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !skuPai) return;

    setUploadingImages(true);
    try {
      const updated = await produtoDadosService.uploadImages(skuPai, files);
      if (updated) {
        setProdutoDados(updated);
        if (onProdutoDadosUpdated) onProdutoDadosUpdated(updated);
      }
    } catch (err) {
      alert("Erro ao fazer upload do arquivo de imagens: " + err.message);
    } finally {
      setUploadingImages(false);
      e.target.value = "";
    }
  };

  // Excluir Imagem individual anexada
  const handleDeleteImage = async (fileName) => {
    if (!window.confirm(`Deseja realmente remover o arquivo "${fileName}" da ficha?`)) return;
    setDeletingFile(fileName);
    try {
      const updated = await produtoDadosService.deleteImage(skuPai, fileName);
      if (updated) {
        setProdutoDados(updated);
        if (onProdutoDadosUpdated) onProdutoDadosUpdated(updated);
      }
    } catch (err) {
      alert("Erro ao remover imagem: " + err.message);
    } finally {
      setDeletingFile(null);
    }
  };

  // Excluir Arquivo 3D individual anexado
  const handleDeleteModelagemFile = async (fileName) => {
    if (!window.confirm(`Deseja realmente remover o arquivo 3D "${fileName}" da ficha?`)) return;
    setDeletingFile(fileName);
    try {
      const updated = await produtoDadosService.deleteModelagemFile(skuPai, fileName);
      if (updated) {
        setProdutoDados(updated);
        if (onProdutoDadosUpdated) onProdutoDadosUpdated(updated);
      }
    } catch (err) {
      alert("Erro ao remover arquivo 3D: " + err.message);
    } finally {
      setDeletingFile(null);
    }
  };

  const hasMlAd = !!(linksLegado?.hasMlAd || linksLegado?.mlUrl || cadRecord?.mercado_livre);
  const storeUrl = linksLegado?.storeUrl || cadRecord?.link_produto || "";

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-6">

      {/* CABEÇALHO DA SEÇÃO + INDICADOR DE REGISTRO NA MODELAGEM CAD */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 border-b border-slate-100 pb-4">
        <div className="flex items-center gap-2.5">
          <div className="p-2 bg-purple-50 text-purple-600 rounded-xl border border-purple-200">
            <Layers size={20} />
          </div>
          <div>
            <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider">
              Modelagem CAD, Anexos & Links (SKU: {skuPai})
            </h3>
            <p className="text-xs text-slate-400">
              Vínculo com a área de Modelagem CAD, arquivos 3D, imagens e links comerciais.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => setIsEditModalOpen(true)}
            className="px-3.5 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer"
          >
            <Edit2 size={13} />
            <span>Editar Links & Observações</span>
          </button>
        </div>
      </div>

      {/* BANNER: EXISTÊNCIA DE REGISTRO NA ÁREA DE MODELAGEM CAD */}
      {loading ? (
        <div className="py-4 text-center text-slate-400 text-xs flex items-center justify-center gap-2">
          <RefreshCw size={15} className="animate-spin text-purple-600" />
          <span>Verificando registro na área de Modelagem CAD...</span>
        </div>
      ) : cadRecord ? (
        <div className="bg-purple-50/70 border border-purple-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-start sm:items-center gap-3">
            <div className="p-2 bg-emerald-100 text-emerald-700 rounded-lg shrink-0">
              <CheckCircle2 size={18} />
            </div>
            <div className="space-y-0.5">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-bold text-slate-900">
                  Possui registro na área de Modelagem CAD
                </span>
                <span className="px-2 py-0.5 bg-purple-100 text-purple-800 border border-purple-200 rounded text-[10px] font-bold uppercase">
                  SKU: {cadRecord.sku}
                </span>
                {cadFilesList.length > 0 && (
                  <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-[10px] font-bold">
                    {cadFilesList.length} arquivo(s) no CAD
                  </span>
                )}
              </div>
              <p className="text-[11px] text-slate-600">
                {cadRecord.nome_produto || `Registro vinculado ao SKU ${skuPai}`}
                {cadRecord.observacoes ? ` — Obs: ${cadRecord.observacoes}` : ""}
              </p>
            </div>
          </div>

          {onNavigateToCad && (
            <button
              type="button"
              onClick={() => onNavigateToCad(skuPai)}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-xl text-xs font-bold shadow-xs transition-all flex items-center gap-1.5 shrink-0 cursor-pointer self-start sm:self-auto"
            >
              <span>Ir para o Fluxo CAD</span>
              <ArrowRight size={14} />
            </button>
          )}
        </div>
      ) : (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-slate-200 text-slate-500 rounded-lg shrink-0">
              <AlertCircle size={18} />
            </div>
            <div>
              <span className="text-xs font-bold text-slate-700 block">
                Nenhum registro deste SKU na área de Modelagem CAD
              </span>
              <p className="text-[11px] text-slate-500">
                Este SKU não possui card ativo ou histórico cadastrado na fila de Modelagem CAD.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0 flex-wrap">
            <button
              type="button"
              onClick={handleCreateAndGoToCad}
              disabled={creatingCad}
              className="px-3.5 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-xl text-xs font-bold shadow-xs transition-all flex items-center gap-1.5 cursor-pointer"
            >
              {creatingCad ? <RefreshCw size={13} className="animate-spin" /> : <Plus size={14} />}
              <span>Criar Registro & Ir ao CAD</span>
            </button>

            {onNavigateToCad && (
              <button
                type="button"
                onClick={() => onNavigateToCad(skuPai)}
                className="px-3.5 py-2 bg-white hover:bg-slate-100 text-slate-700 border border-slate-300 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer"
              >
                <span>Abrir Modelagem CAD</span>
                <ArrowRight size={13} />
              </button>
            )}
          </div>
        </div>
      )}

      {/* LINKS DO PRODUTO */}
      {!loading && (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">
              Links do Produto & Mercado Livre
            </span>
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-slate-600">Anúncio Mercado Livre:</span>
              {hasMlAd ? (
                <span className="px-2.5 py-0.5 bg-amber-100 text-amber-900 border border-amber-300 rounded-md text-[10px] font-bold flex items-center gap-1">
                  <Store size={12} className="text-amber-600" />
                  Ativo no ML
                </span>
              ) : (
                <span className="px-2 py-0.5 bg-slate-200 text-slate-600 rounded-md text-[10px] font-medium">
                  Sem Anúncio ML
                </span>
              )}
            </div>
          </div>

          <div className="flex flex-wrap gap-2 text-xs">
            {storeUrl ? (
              <a
                href={storeUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-1.5 bg-purple-50 text-purple-700 hover:bg-purple-100 border border-purple-200 rounded-lg font-medium flex items-center gap-1.5 transition-colors"
              >
                <ShoppingBag size={14} />
                <span>Anúncio na Loja</span>
                <ExternalLink size={12} />
              </a>
            ) : null}

            {linksLegado?.mlUrl ? (
              <a
                href={linksLegado.mlUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-1.5 bg-amber-50 text-amber-800 hover:bg-amber-100 border border-amber-300 rounded-lg font-bold flex items-center gap-1.5 transition-colors"
              >
                <Store size={14} className="text-amber-600" />
                <span>Anúncio no Mercado Livre</span>
                <ExternalLink size={12} />
              </a>
            ) : null}

            {linksLegado?.driveUrl ? (
              <a
                href={linksLegado.driveUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-1.5 bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 rounded-lg font-medium flex items-center gap-1.5 transition-colors"
              >
                <Folder size={14} />
                <span>Pasta do Google Drive</span>
                <ExternalLink size={12} />
              </a>
            ) : null}

            {linksLegado?.youtubeUrl ? (
              <a
                href={linksLegado.youtubeUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-1.5 bg-red-50 text-red-700 hover:bg-red-100 border border-red-200 rounded-lg font-medium flex items-center gap-1.5 transition-colors"
              >
                <Video size={14} />
                <span>Vídeo no YouTube</span>
                <ExternalLink size={12} />
              </a>
            ) : null}

            {!storeUrl && !linksLegado?.mlUrl && !linksLegado?.driveUrl && !linksLegado?.youtubeUrl && (
              <div className="text-xs text-slate-400 italic">
                Nenhum link cadastrado. Clique em "Editar Links & Observações" para preencher.
              </div>
            )}
          </div>

          {(produtoDados?.observacoes_internas || cadRecord?.observacoes) && (
            <p className="text-xs text-slate-600 bg-white p-2.5 rounded border border-slate-200 mt-2">
              <strong className="font-semibold text-slate-800 block mb-0.5">Observações:</strong>
              {produtoDados?.observacoes_internas || cadRecord?.observacoes}
            </p>
          )}
        </div>
      )}

      {/* LISTAGEM E UPLOAD DE ARQUIVOS DE MODELAGEM 3D */}
      <div className="space-y-3 border-t border-slate-100 pt-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center gap-1.5">
            <FileCode size={16} className="text-purple-600" />
            Arquivos de Modelagem 3D ({modelagemFilesList.length})
          </h4>

          <label className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-bold text-xs shadow-xs transition-all flex items-center gap-1.5 cursor-pointer">
            {uploading ? (
              <>
                <RefreshCw size={14} className="animate-spin" />
                <span>Enviando...</span>
              </>
            ) : (
              <>
                <Upload size={14} />
                <span>+ Enviar Arquivos 3D (.stl, .3dm)</span>
              </>
            )}
            <input
              type="file"
              multiple
              accept=".stl,.3dm,.obj,.zip,.png,.jpg,.jpeg,.pdf"
              onChange={handleFileUpload}
              disabled={uploading}
              className="hidden"
            />
          </label>
        </div>

        {modelagemFilesList.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            {modelagemFilesList.map((itemObj, idx) => {
              const fileName = itemObj.name;
              const fileUrl = pb.files.getUrl(itemObj.record, fileName);
              const isCadOrigin = itemObj.origin === "CAD";

              return (
                <div key={idx} className={`bg-slate-50 border rounded-lg p-3 flex items-center justify-between text-xs hover:border-purple-300 transition-colors ${isCadOrigin ? "border-purple-300 bg-purple-50/20 shadow-xs" : "border-slate-200"}`}>
                  <div className="flex items-center gap-2 truncate">
                    <FileCode size={16} className="text-purple-600 shrink-0" />
                    <span className="font-mono font-medium text-slate-800 truncate" title={fileName}>
                      {fileName}
                    </span>
                    {isCadOrigin && (
                      <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 font-bold rounded text-[9px] uppercase tracking-wide">CAD</span>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <a
                      href={fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-1 text-slate-400 hover:text-purple-600 transition-colors"
                      title="Baixar Arquivo 3D"
                    >
                      <Download size={15} />
                    </a>
                    {!isCadOrigin && (
                      <button
                        type="button"
                        onClick={() => handleDeleteModelagemFile(fileName)}
                        disabled={deletingFile === fileName}
                        className="p-1 text-slate-400 hover:text-red-600 transition-colors cursor-pointer disabled:opacity-50"
                        title="Remover Arquivo 3D da Ficha"
                      >
                        {deletingFile === fileName ? <RefreshCw size={13} className="animate-spin text-red-500" /> : <Trash2 size={14} />}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl text-center text-xs text-slate-400">
            Nenhum arquivo de modelagem 3D anexado até o momento.
          </div>
        )}
      </div>

      {/* LISTAGEM E UPLOAD DE ARQUIVOS DE IMAGENS (ZIP / RAR / IMAGENS) */}
      <div className="space-y-3 border-t border-slate-100 pt-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center gap-1.5">
            <Folder size={16} className="text-blue-600" />
            Arquivos de Imagens & Pacotes ({imagesFilesList.length})
          </h4>

          <label className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold text-xs shadow-xs transition-all flex items-center gap-1.5 cursor-pointer">
            {uploadingImages ? (
              <>
                <RefreshCw size={14} className="animate-spin" />
                <span>Enviando...</span>
              </>
            ) : (
              <>
                <Upload size={14} />
                <span>+ Enviar Imagens / Pacote (.zip, .png, .jpg)</span>
              </>
            )}
            <input
              type="file"
              multiple
              accept=".zip,.rar,.tar,.gz,.png,.jpg,.jpeg,.webp"
              onChange={handleImagesUpload}
              disabled={uploadingImages}
              className="hidden"
            />
          </label>
        </div>

        {imagesFilesList.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            {imagesFilesList.map((itemObj, idx) => {
              const fileName = itemObj.name;
              const fileUrl = pb.files.getUrl(itemObj.record, fileName);
              const isCadOrigin = itemObj.origin === "CAD";

              return (
                <div key={idx} className={`bg-slate-50 border rounded-lg p-3 flex items-center justify-between text-xs hover:border-blue-300 transition-colors ${isCadOrigin ? "border-blue-300 bg-blue-50/20 shadow-xs" : "border-slate-200"}`}>
                  <div className="flex items-center gap-2 truncate">
                    <Folder size={16} className="text-blue-600 shrink-0" />
                    <span className="font-mono font-medium text-slate-800 truncate" title={fileName}>
                      {fileName}
                    </span>
                    {isCadOrigin && (
                      <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 font-bold rounded text-[9px] uppercase tracking-wide">CAD</span>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <a
                      href={fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-1 text-slate-400 hover:text-blue-600 transition-colors"
                      title="Abrir / Baixar Arquivo"
                    >
                      <Download size={15} />
                    </a>
                    {!isCadOrigin && (
                      <button
                        type="button"
                        onClick={() => handleDeleteImage(fileName)}
                        disabled={deletingFile === fileName}
                        className="p-1 text-slate-400 hover:text-red-600 transition-colors cursor-pointer disabled:opacity-50"
                        title="Remover Imagem da Ficha"
                      >
                        {deletingFile === fileName ? <RefreshCw size={13} className="animate-spin text-red-500" /> : <Trash2 size={14} />}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl text-center text-xs text-slate-400">
            Nenhum pacote ou arquivo de imagem anexado até o momento.
          </div>
        )}
      </div>

      {/* MODAL DE EDIÇÃO DE LINKS E DETALHES */}
      {isEditModalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-lg overflow-hidden flex flex-col">
            <div className="bg-slate-900 text-white px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Edit2 size={18} className="text-purple-400" />
                <h3 className="font-bold text-sm">Editar Links & Detalhes (SKU: {skuPai})</h3>
              </div>
              <button onClick={() => setIsEditModalOpen(false)} className="text-slate-400 hover:text-white cursor-pointer">
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSaveLinks} className="p-6 space-y-4 text-xs">
              <div>
                <label className="block font-bold text-slate-700 mb-1 uppercase tracking-wider text-[10px]">
                  Link do Produto na Loja
                </label>
                <input
                  type="url"
                  value={formLinks.storeUrl}
                  onChange={(e) => setFormLinks({ ...formLinks, storeUrl: e.target.value })}
                  placeholder="https://sualoja.com.br/produto..."
                  className="w-full bg-slate-50 border border-slate-300 rounded-lg p-2 font-medium text-slate-800 outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1 uppercase tracking-wider text-[10px]">
                  Link do Anúncio no Mercado Livre (ML)
                </label>
                <input
                  type="url"
                  value={formLinks.mlUrl}
                  onChange={(e) => setFormLinks({
                    ...formLinks,
                    mlUrl: e.target.value,
                    hasMlAd: !!e.target.value || formLinks.hasMlAd
                  })}
                  placeholder="https://produto.mercadolivre.com.br/MLB..."
                  className="w-full bg-slate-50 border border-slate-300 rounded-lg p-2 font-medium text-slate-800 outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>

              <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 p-3 rounded-lg">
                <input
                  type="checkbox"
                  id="hasMlAd"
                  checked={formLinks.hasMlAd}
                  onChange={(e) => setFormLinks({ ...formLinks, hasMlAd: e.target.checked })}
                  className="w-4 h-4 text-amber-600 rounded cursor-pointer"
                />
                <label htmlFor="hasMlAd" className="font-bold text-amber-900 cursor-pointer text-xs select-none">
                  Este produto possui anúncio ativo no Mercado Livre (ML)
                </label>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-bold text-slate-700 mb-1 uppercase tracking-wider text-[10px]">
                    Link Google Drive
                  </label>
                  <input
                    type="url"
                    value={formLinks.driveUrl}
                    onChange={(e) => setFormLinks({ ...formLinks, driveUrl: e.target.value })}
                    placeholder="https://drive.google.com/..."
                    className="w-full bg-slate-50 border border-slate-300 rounded-lg p-2 font-medium text-slate-800 outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <div>
                  <label className="block font-bold text-slate-700 mb-1 uppercase tracking-wider text-[10px]">
                    Link Vídeo YouTube
                  </label>
                  <input
                    type="url"
                    value={formLinks.youtubeUrl}
                    onChange={(e) => setFormLinks({ ...formLinks, youtubeUrl: e.target.value })}
                    placeholder="https://youtube.com/..."
                    className="w-full bg-slate-50 border border-slate-300 rounded-lg p-2 font-medium text-slate-800 outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1 uppercase tracking-wider text-[10px]">
                  Observações de Modelagem / Fabricação
                </label>
                <textarea
                  rows="3"
                  value={formLinks.observacoes}
                  onChange={(e) => setFormLinks({ ...formLinks, observacoes: e.target.value })}
                  placeholder="Observações internas sobre este produto..."
                  className="w-full bg-slate-50 border border-slate-300 rounded-lg p-2 font-medium text-slate-800 outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                />
              </div>

              <div className="pt-4 flex items-center justify-end gap-2 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsEditModalOpen(false)}
                  className="px-4 py-2 text-slate-600 font-semibold cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={savingLinks}
                  className="px-5 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-bold transition-all shadow-md flex items-center gap-1.5 cursor-pointer"
                >
                  {savingLinks ? (
                    <>
                      <RefreshCw size={14} className="animate-spin" />
                      <span>Salvando...</span>
                    </>
                  ) : (
                    <>
                      <Save size={14} />
                      <span>Salvar Detalhes</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}
